import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface MushroomPart {
  name: string;
  label: string;
  color: string;
}

interface IdentificationCard {
  category: string;
  species: string;
  tip: string;
  color: string;
  icon: string;
}

interface MedicinalFungus {
  name: string;
  benefit: string;
  color: string;
}

interface DecompStep {
  label: string;
  icon: string;
}

interface MushroomTemplateProps {
  topic: string;
  subtitle: string;
  mushroomParts: MushroomPart[];
  identificationCards: IdentificationCard[];
  medicinalFungi: MedicinalFungus[];
  decompCycle: DecompStep[];
  toxicComparison: { label: string; edible: string; toxic: string }[];
  stats: { value: string; label: string }[];
  bottomWisdom: string;
}

const DEFAULT_PROPS: MushroomTemplateProps = {
  topic: '蘑菇学与真菌王国',
  subtitle: 'Mycology & the Fungal Kingdom',
  mushroomParts: [
    { name: '菌盖', label: 'Cap (Pileus)', color: '#78350f' },
    { name: '菌褶', label: 'Gills (Lamellae)', color: '#ec4899' },
    { name: '菌环', label: 'Ring (Annulus)', color: '#fbbf24' },
    { name: '菌柄', label: 'Stipe (Stalk)', color: '#fef3c7' },
    { name: '菌托', label: 'Volva (Cup)', color: '#94a3b8' },
    { name: '菌丝体', label: 'Mycelium', color: '#f0f9ff' },
  ],
  identificationCards: [
    {
      category: '可食用',
      species: '香菇 Lentinula edodes',
      tip: '菌盖表面有鳞片，菌褶白色整齐，气味芳香',
      color: '#22c55e',
      icon: '✅',
    },
    {
      category: '有毒',
      species: '毒鹅膏 Amanita phalloides',
      tip: '具菌环+菌托，白色菌褶，基部膨大呈球状',
      color: '#dc2626',
      icon: '☠️',
    },
    {
      category: '药用',
      species: '灵芝 Ganoderma lucidum',
      tip: '木质坚硬，表面有漆样光泽，无菌褶有管孔',
      color: '#a855f7',
      icon: '💊',
    },
    {
      category: '共生',
      species: '松茸 Tricholoma matsutake',
      tip: '与松树根系共生，菌盖棕色纤维状，浓郁香气',
      color: '#f97316',
      icon: '🤝',
    },
  ],
  medicinalFungi: [
    { name: '灵芝', benefit: '增强免疫力、抗肿瘤', color: '#a855f7' },
    { name: '冬虫夏草', benefit: '补肺益肾、调节免疫', color: '#fbbf24' },
    { name: '茯苓', benefit: '健脾利湿、宁心安神', color: '#f0f9ff' },
    { name: '猴头菇', benefit: '养胃护胃、促进消化', color: '#fb923c' },
  ],
  decompCycle: [
    { label: '落叶', icon: '🍂' },
    { label: '真菌分解', icon: '🍄' },
    { label: '营养释放', icon: '💧' },
    { label: '植物吸收', icon: '🌱' },
  ],
  toxicComparison: [
    { label: '菌环', edible: '多数无菌环', toxic: '常有明显菌环' },
    { label: '颜色变化', edible: '切开不变色', toxic: '切开后变色' },
    { label: '气味', edible: '清香或无味', toxic: '刺鼻异味' },
  ],
  stats: [
    { value: '15万+', label: '全球已知真菌物种' },
    { value: '10%', label: '蘑菇占真菌比例' },
    { value: '10%', label: '菌丝体覆盖土壤' },
  ],
  bottomWisdom:
    '真菌是地球隐秘的建筑师——编织地下网络，连接万物，驱动生态循环',
};

export const MushroomTemplate: React.FC<Partial<MushroomTemplateProps>> = (
  props,
) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const anatomyStart = 30;
  const cardStart = 100;
  const sporeStart = 60;
  const networkStart = 180;
  const decompStart = 260;
  const comparisonStart = 320;
  const medicinalStart = 390;
  const statsStart = 460;
  const wisdomStart = 510;

  const mushroomGrow = interpolate(frame, [anatomyStart, anatomyStart + 60], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const capUnfurl = interpolate(frame, [anatomyStart + 40, anatomyStart + 70], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const wisdomOpacity = interpolate(
    frame,
    [wisdomStart, wisdomStart + 30],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const sporeCount = 18;
  const sporePhase = interpolate(frame, [sporeStart, sporeStart + 300], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const networkDraw = interpolate(frame, [networkStart, networkStart + 60], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0f140a 0%, #0a0f06 100%)',
        fontFamily:
          '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Mycelium thread pattern background */}
      <svg
        style={{ position: 'absolute', top: 0, left: 0 }}
        width="1920"
        height="1080"
        viewBox="0 0 1920 1080"
      >
        {Array.from({ length: 20 }).map((_, i) => {
          const x1 = (i * 237) % 1920;
          const y1 = 200 + (i * 67) % 880;
          const x2 = (x1 + 180 + (i * 63) % 300) % 1920;
          const y2 = y1 + 30 + (i * 31) % 60;
          const cx = (x1 + x2) / 2 + ((i % 2 === 0 ? 1 : -1) * 50);
          const cy = (y1 + y2) / 2 - 20;
          return (
            <path
              key={`thread-${i}`}
              d={`M${x1},${y1} Q${cx},${cy} ${x2},${y2}`}
              fill="none"
              stroke="#f0f9ff"
              strokeWidth="0.8"
              opacity={0.04}
            />
          );
        })}
      </svg>

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: 36,
          right: 36,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 16}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <div
          style={{
            padding: '5px 14px',
            background: '#1a2e0a',
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 700,
            color: '#22c55e',
            border: '1px solid #22c55e30',
            whiteSpace: 'nowrap',
          }}
        >
          🍄 MYCOLOGY
        </div>
        <h1
          style={{
            fontSize: 34,
            fontWeight: 900,
            margin: 0,
            color: '#f8fafc',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p style={{ fontSize: 24, color: '#64748b', margin: 0 }}>
          {p.subtitle}
        </p>
      </div>

      {/* ===== Mushroom Anatomy SVG ===== */}
      <svg
        style={{ position: 'absolute', left: 24, top: 80 }}
        width="900"
        height="520"
        viewBox="0 0 900 520"
      >
        <defs>
          <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#92400e" />
            <stop offset="100%" stopColor="#78350f" />
          </linearGradient>
          <linearGradient id="stipeGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="100%" stopColor="#fde68a" />
          </linearGradient>
          <linearGradient id="soilGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1c1917" />
            <stop offset="100%" stopColor="#0c0a09" />
          </linearGradient>
        </defs>

        {/* Ground line */}
        <rect
          x="0"
          y="340"
          width="900"
          height="180"
          fill="url(#soilGrad)"
          opacity={0.6}
        />
        <line
          x1="0"
          y1="340"
          x2="900"
          y2="340"
          stroke="#166534"
          strokeWidth="2"
          opacity={0.4}
        />

        {/* Mycelium underground network */}
        {[
          'M250,380 Q300,400 350,370 Q400,350 450,390 Q500,420 550,380',
          'M200,420 Q280,440 360,410 Q420,390 500,430',
          'M300,450 Q380,470 460,440 Q520,420 580,460',
          'M180,400 Q220,430 280,400 Q340,380 380,410',
          'M450,400 Q510,430 570,400 Q620,380 660,410',
        ].map((d, i) => (
          <path
            key={`myc-${i}`}
            d={d}
            fill="none"
            stroke="#f0f9ff"
            strokeWidth="1.5"
            opacity={networkDraw * 0.5}
            strokeDasharray="300"
            strokeDashoffset={300 * (1 - networkDraw)}
          />
        ))}

        {/* Mycelium nodes */}
        {[
          [250, 380], [350, 370], [450, 390], [550, 380],
          [280, 400], [360, 410], [500, 430], [460, 440],
        ].map(([cx, cy], i) => (
          <circle
            key={`node-${i}`}
            cx={cx}
            cy={cy}
            r={3}
            fill="#f0f9ff"
            opacity={networkDraw * 0.6}
          />
        ))}

        {/* Volva (base cup) */}
        <ellipse
          cx="420"
          cy={340 - 5 * mushroomGrow}
          rx={28 * mushroomGrow}
          ry={12 * mushroomGrow}
          fill="#94a3b8"
          opacity={mushroomGrow * 0.7}
          stroke="#64748b"
          strokeWidth="1"
        />

        {/* Stipe (stalk) — grows upward */}
        <rect
          x="405"
          y={340 - 160 * mushroomGrow}
          width="30"
          height={160 * mushroomGrow}
          rx="8"
          fill="url(#stipeGrad)"
          opacity={mushroomGrow}
        />

        {/* Ring (annulus) */}
        <ellipse
          cx="420"
          cy={340 - 120 * mushroomGrow}
          rx={22 * mushroomGrow}
          ry={6 * mushroomGrow}
          fill="none"
          stroke="#fbbf24"
          strokeWidth="2"
          opacity={mushroomGrow * 0.8}
        />

        {/* Gills (lamellae) — under cap */}
        {Array.from({ length: 9 }).map((_, i) => {
          const angle = (-80 + i * 20) * (Math.PI / 180);
          const cx = 420;
          const cy = 340 - 155 * mushroomGrow;
          const len = 45 * capUnfurl;
          return (
            <line
              key={`gill-${i}`}
              x1={cx}
              y1={cy}
              x2={cx + Math.cos(angle) * len}
              y2={cy + Math.sin(angle) * len * 0.4 + 8}
              stroke="#ec4899"
              strokeWidth="1.5"
              opacity={capUnfurl * 0.7}
            />
          );
        })}

        {/* Cap (pileus) */}
        <ellipse
          cx="420"
          cy={340 - 160 * mushroomGrow - 15 * capUnfurl}
          rx={30 + 55 * capUnfurl}
          ry={15 + 10 * capUnfurl}
          fill="url(#capGrad)"
          opacity={mushroomGrow}
          stroke="#92400e"
          strokeWidth="1.5"
        />

        {/* Spore dots releasing from gills */}
        {Array.from({ length: sporeCount }).map((_, i) => {
          const baseX = 380 + (i * 17) % 80;
          const startY = 340 - 140 * mushroomGrow;
          const fallDist = 60 + (i * 23) % 80;
          const localPhase = (sporePhase + i * 0.06) % 1;
          const sx = baseX + Math.sin(localPhase * Math.PI * 4 + i) * 15;
          const sy = startY + localPhase * fallDist;
          const sporeOpacity = interpolate(
            localPhase,
            [0, 0.1, 0.8, 1],
            [0, 0.7, 0.4, 0],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          return (
            <circle
              key={`spore-${i}`}
              cx={sx}
              cy={sy}
              r={1.5}
              fill="#ec4899"
              opacity={sporeOpacity * capUnfurl}
            />
          );
        })}

        {/* Anatomy labels with leader lines */}
        {[
          { part: '菌盖', x: 420, y: 340 - 160 * mushroomGrow - 15 * capUnfurl, lx: 600, ly: 80, col: '#78350f' },
          { part: '菌褶', x: 420, y: 340 - 145 * mushroomGrow, lx: 620, ly: 140, col: '#ec4899' },
          { part: '菌环', x: 420, y: 340 - 120 * mushroomGrow, lx: 640, ly: 200, col: '#fbbf24' },
          { part: '菌柄', x: 420, y: 340 - 80 * mushroomGrow, lx: 620, ly: 260, col: '#fef3c7' },
          { part: '菌托', x: 420, y: 340, lx: 600, ly: 320, col: '#94a3b8' },
          { part: '菌丝体', x: 420, y: 400, lx: 640, ly: 400, col: '#f0f9ff' },
        ].map((lbl, i) => {
          const labelOpacity = interpolate(
            frame,
            [anatomyStart + 30 + i * 10, anatomyStart + 45 + i * 10],
            [0, 1],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          return (
            <g key={`lbl-${i}`} opacity={labelOpacity}>
              <line
                x1={lbl.x + 40}
                y1={lbl.y}
                x2={lbl.lx}
                y2={lbl.ly}
                stroke={lbl.col}
                strokeWidth="1"
                strokeDasharray="4 3"
                opacity={0.6}
              />
              <circle cx={lbl.lx} cy={lbl.ly} r="4" fill={lbl.col} />
              <text
                x={lbl.lx + 14}
                y={lbl.ly + 5}
                fill={lbl.col}
                fontSize="20"
                fontWeight="700"
              >
                {lbl.part}
              </text>
            </g>
          );
        })}

        {/* Tree root icons for "木联网" */}
        {[180, 420, 660].map((tx, i) => {
          const treeOpacity = interpolate(
            frame,
            [networkStart + 20 + i * 15, networkStart + 40 + i * 15],
            [0, 0.8],
            { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
          );
          return (
            <g key={`tree-${i}`} opacity={treeOpacity}>
              <line
                x1={tx}
                y1={340}
                x2={tx}
                y2={310}
                stroke="#166534"
                strokeWidth="3"
              />
              <circle cx={tx} cy={300} r="14" fill="#166534" opacity={0.7} />
              <text
                x={tx}
                y={305}
                textAnchor="middle"
                fill="#22c55e"
                fontSize="14"
              >
                🌲
              </text>
            </g>
          );
        })}

        {/* "木联网" label */}
        <text
          x="420"
          y="495"
          textAnchor="middle"
          fill="#f0f9ff"
          fontSize="24"
          fontWeight="600"
          opacity={networkDraw * 0.8}
        >
          菌丝网络 · "木联网" — 地下信息高速公路
        </text>
      </svg>

      {/* ===== Identification Guide Cards ===== */}
      <div
        style={{
          position: 'absolute',
          top: 80,
          left: 970,
          right: 36,
          width: 910,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 12,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          鉴别指南
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {p.identificationCards.map((card, i) => {
            const s = spring({
              frame: frame - (cardStart + i * 18),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  width: 'calc(50% - 5px)',
                  background: '#111a0a',
                  borderRadius: 12,
                  padding: '12px 14px',
                  borderLeft: `3px solid ${card.color}`,
                  opacity: s,
                  transform: `translateY(${(1 - s) * 20}px)`,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontSize: 20 }}>{card.icon}</span>
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: card.color,
                    }}
                  >
                    {card.category}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#e2e8f0',
                    marginBottom: 4,
                  }}
                >
                  {card.species}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    color: '#94a3b8',
                    lineHeight: 1.5,
                  }}
                >
                  {card.tip}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Edible vs Toxic Comparison ===== */}
      <div
        style={{
          position: 'absolute',
          top: 450,
          left: 970,
          right: 36,
          width: 910,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 10,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          可食用 vs 有毒 鉴别要点
        </div>
        <div
          style={{
            background: '#111a0a',
            borderRadius: 12,
            padding: '12px 16px',
            border: '1px solid #1e3a0e',
          }}
        >
          {/* Table header */}
          <div
            style={{
              display: 'flex',
              gap: 10,
              marginBottom: 8,
              paddingBottom: 8,
              borderBottom: '1px solid #1e3a0e',
            }}
          >
            <div style={{ flex: 1, fontSize: 22, fontWeight: 700, color: '#64748b' }}>特征</div>
            <div style={{ flex: 1.5, fontSize: 22, fontWeight: 700, color: '#22c55e' }}>✅ 可食用</div>
            <div style={{ flex: 1.5, fontSize: 22, fontWeight: 700, color: '#dc2626' }}>☠️ 有毒</div>
          </div>
          {p.toxicComparison.map((row, i) => {
            const rowOpacity = interpolate(
              frame,
              [comparisonStart + i * 15, comparisonStart + i * 15 + 20],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '6px 0',
                  opacity: rowOpacity,
                }}
              >
                <div style={{ flex: 1, fontSize: 22, fontWeight: 700, color: '#fbbf24' }}>
                  {row.label}
                </div>
                <div style={{ flex: 1.5, fontSize: 22, color: '#22c55e' }}>
                  {row.edible}
                </div>
                <div style={{ flex: 1.5, fontSize: 22, color: '#dc2626' }}>
                  {row.toxic}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Decomposition Cycle ===== */}
      <div
        style={{
          position: 'absolute',
          top: 640,
          left: 160,
          right: 36,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 12,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          分解循环
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 0,
          }}
        >
          {p.decompCycle.map((step, i) => {
            const s = spring({
              frame: frame - (decompStart + i * 18),
              fps,
              config: { damping: 12, stiffness: 140 },
            });
            const isLast = i === p.decompCycle.length - 1;
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    opacity: s,
                    transform: `scale(${s})`,
                  }}
                >
                  <div
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: '#1a2e0a',
                      border: '2px solid #166534',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 40,
                    }}
                  >
                    {step.icon}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: '#22c55e',
                      marginTop: 8,
                      textAlign: 'center',
                    }}
                  >
                    {step.label}
                  </div>
                </div>
                {!isLast && (
                  <div
                    style={{
                      width: 60,
                      height: 2,
                      background:
                        'linear-gradient(90deg, #166534, #16653440)',
                      margin: '0 8px',
                      marginBottom: 30,
                      opacity: interpolate(
                        frame,
                        [decompStart + i * 18 + 10, decompStart + i * 18 + 25],
                        [0, 0.7],
                        {
                          extrapolateLeft: 'clamp',
                          extrapolateRight: 'clamp',
                        },
                      ),
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
          {/* Circular arrow back to start */}
          <div
            style={{
              fontSize: 26,
              color: '#166534',
              marginLeft: 12,
              marginBottom: 30,
              opacity: interpolate(
                frame,
                [decompStart + 80, decompStart + 95],
                [0, 0.6],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              ),
            }}
          >
            ↻
          </div>
        </div>
      </div>

      {/* ===== Medicinal Fungi ===== */}
      <div
        style={{
          position: 'absolute',
          top: 730,
          left: 970,
          right: 36,
          width: 910,
        }}
      >
        <div
          style={{
            fontSize: 26,
            color: '#64748b',
            marginBottom: 10,
            letterSpacing: '0.08em',
            fontWeight: 600,
          }}
        >
          药用真菌
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {p.medicinalFungi.map((mf, i) => {
            const s = spring({
              frame: frame - (medicinalStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  width: 'calc(50% - 5px)',
                  background: '#111a0a',
                  borderRadius: 12,
                  padding: '12px 14px',
                  border: `1px solid ${mf.color}30`,
                  opacity: s,
                  transform: `scale(${0.85 + 0.15 * s})`,
                }}
              >
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: mf.color,
                    marginBottom: 4,
                  }}
                >
                  {mf.name}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    color: '#94a3b8',
                    lineHeight: 1.5,
                  }}
                >
                  {mf.benefit}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== Stats ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 130,
          left: 24,
          width: 900,
          display: 'flex',
          gap: 10,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#111a0a',
                borderRadius: 12,
                padding: '14px 8px',
                textAlign: 'center',
                border: '1px solid #1e3a0e',
                opacity: stIn,
                transform: `scale(${0.85 + 0.15 * stIn})`,
              }}
            >
              <div
                style={{
                  fontSize: 30,
                  fontWeight: 900,
                  color: '#22c55e',
                }}
              >
                {st.value}
              </div>
              <div
                style={{
                  fontSize: 22,
                  color: '#64748b',
                  marginTop: 4,
                }}
              >
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Wisdom (OPAQUE background) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#0f1a0a',
          borderRadius: 14,
          padding: '12px 20px',
          borderTop: '3px solid #166534',
          opacity: wisdomOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 26 }}>🍄</span>
          <span
            style={{
              fontSize: 19,
              color: '#e2e8f0',
              lineHeight: 1.6,
            }}
          >
            {p.bottomWisdom}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background:
            'linear-gradient(90deg, #166534, #22c55e, #78350f, #ec4899)',
        }}
      />
    </AbsoluteFill>
  );
};
